import React from 'react';
import { Link } from 'react-router-dom';
import Section from './ui/Section';
import Button from './ui/button';

// Service card data
const services = [
  {
    title: "Find, engage & grow",
    description: "Our UK database, Resident, provides permissioned access to over 52 million individuals, with 400 variables enabling precise, multi-sector targeting. It's the foundation for intelligent, results-driven marketing."
  },
  {
    title: "Cleaner data, better results",
    description: "Our advanced cleansing and suppression services remove outdated, duplicate, or inactive records, boosting accuracy, cutting waste, and driving better marketing results. Clean data isn't optional, it's essential."
  },
  {
    title: "Optimise & target smarter",
    description: "Maximise marketing impact with data-driven insights, data enhancement, refined segmentation, and seamless multi-channel targeting. We help transform data into campaigns that convert."
  }
];

// Statistics data
const statistics = [
  {
    icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
    number: "51 Million",
    label: "Individuals"
  },
  {
    icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
    number: "31 Million",
    label: "Postal addresses"
  },
  {
    icon: "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
    number: "13 Million",
    label: "Phone numbers"
  },
  {
    icon: "M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207",
    number: "14 Million",
    label: "Email addresses"
  }
];

// Reusable components
const ServiceCard = ({ title, description }) => (
  <div className="bg-white p-4 sm:p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300">
    <h3 className="text-base sm:text-xl font-bold text-gray-800 mb-2 sm:mb-4">{title}</h3>
    <p className="text-sm md:text-base text-gray-700 leading-relaxed">{description}</p>
  </div>
);

const StatisticItem = ({ icon, number, label }) => (
  <div className="text-center">
    <div className="mb-3 sm:mb-4" style={{ color: '#db0058' }}>
      <svg className="w-8 h-8 sm:w-12 sm:h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
      </svg>
    </div>
    <div className="text-lg sm:text-2xl font-bold text-gray-800">{number}</div>
    <div className="text-sm sm:text-base text-gray-600">{label}</div>
  </div>
);

const SectionHeader = ({ title, subtitle, description }) => (
  <header className="text-center mb-6 sm:mb-12">
    <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-3 sm:mb-6">
      {title} <span style={{ color: '#db0058' }}>{subtitle}</span>
    </h2>
    <p className="text-sm sm:text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed px-3 sm:px-0">
      {description}
    </p>
  </header>
);

const DataServices = () => {
  return (
    <Section background="gray-dark" padding="default">
      {/* Header */}
      <SectionHeader
        title="Data services"
        subtitle="that deliver."
        description="We provide end-to-end data services, from acquisition and cleansing to enhancement and analysis. Our solutions help you reach the right audience, maintain data accuracy, and make smarter marketing decisions."
      />

      {/* Service Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6 mb-8 sm:mb-16">
        {services.map((service, index) => (
          <ServiceCard 
            key={index} 
            title={service.title} 
            description={service.description} 
          />
        ))}
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-8 mb-6 sm:mb-12">
        {statistics.map((stat, index) => (
          <StatisticItem
            key={index}
            icon={stat.icon}
            number={stat.number}
            label={stat.label}
          />
        ))}
      </div>

      {/* CTA Button */}
      <div className="text-center">
        <Link to="/contact" onClick={() => window.scrollTo(0, 0)}>
          <Button variant="primary" size="medium">
            Find Out More
          </Button>
        </Link>
      </div>
    </Section>
  );
};

export default DataServices;